import type { RawQuestion } from '@/features/content/useComponent';
import {
  evaluateVariableBasedAnswer,
  resolveRepresentativeVariableValues,
} from '@/features/questions/randomVariables';

import type {
  AddableQuestionType,
  AddableQuestionPartType,
  EditableGradingRubric,
  EditableQuestion,
  EditableQuestionPart,
  EditableRubricUnit,
  SupportedQuestionType,
} from './editorTypes';

function createGeneratedId(prefix: string) {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }

  return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

function normalizeString(value: string | undefined) {
  const normalized = value?.trim();
  return normalized ? normalized : undefined;
}

function hasVariableBasedAnswer(value: string | undefined) {
  return Boolean(normalizeString(value));
}

export function getRubricTotalScore(rubric: EditableGradingRubric | undefined) {
  return (rubric?.rubricUnits ?? []).reduce(
    (total, unit) => total + (Number.isFinite(unit.score) ? unit.score : 0),
    0,
  );
}

export function createDraftRubricUnit(
  index = 0,
  overrides: Partial<EditableRubricUnit> = {},
): EditableRubricUnit {
  return {
    details: '',
    id: createGeneratedId('rubric-unit'),
    name: index === 0 ? 'Correctness' : `Criterion ${index + 1}`,
    score: 1,
    ...overrides,
  };
}

export function createDraftGradingRubric(
  units: EditableRubricUnit[] = [createDraftRubricUnit()],
): EditableGradingRubric {
  return {
    rubricUnits: units,
    totalScore: units.reduce(
      (total, unit) => total + (Number.isFinite(unit.score) ? unit.score : 0),
      0,
    ),
  };
}

export function normalizeGradingRubric(
  rubric: EditableGradingRubric | undefined,
): EditableGradingRubric {
  const units =
    rubric?.rubricUnits && rubric.rubricUnits.length > 0
      ? rubric.rubricUnits.map((unit, index) => ({
          ...createDraftRubricUnit(index),
          ...unit,
          details: unit.details ?? '',
          name: unit.name ?? '',
          score: Number.isFinite(unit.score) ? unit.score : 0,
        }))
      : [createDraftRubricUnit()];

  return createDraftGradingRubric(units);
}

function cloneGradingRubric(
  rubric: EditableGradingRubric | undefined,
): EditableGradingRubric | undefined {
  if (!rubric) {
    return undefined;
  }

  return {
    rubricUnits: rubric.rubricUnits.map((unit) => ({ ...unit })),
    totalScore: rubric.totalScore,
  };
}

export function cloneQuestionPart(
  part: EditableQuestionPart,
): EditableQuestionPart {
  return {
    ...part,
    gradingRubric: cloneGradingRubric(part.gradingRubric),
    options: [...part.options],
    detailedAnswerImages: part.detailedAnswerImages
      ? part.detailedAnswerImages.map((image) => ({ ...image }))
      : undefined,
    hintImages: part.hintImages
      ? part.hintImages.map((image) => ({ ...image }))
      : undefined,
    promptImages: part.promptImages
      ? part.promptImages.map((image) => ({ ...image }))
      : undefined,
  };
}

// `Question.image_paths` is deprecated in the proto in favour of
// `content_section`, and its images are bare URLs: no alt text, no size, no
// caption, and invisible to the AI hint and practice builders, which read the
// prompt's content units only.
//
// Rather than give them a second editor, opening a question folds them into the
// prompt images. They are just URLs, so the conversion is total, and saving
// writes them back as real content units — the corpus drains through ordinary
// editing with no migration job. The editor therefore shows ONE image list.
function foldLegacyImagePaths(question: EditableQuestion) {
  const legacyPaths = question.imagePaths ?? [];

  if (legacyPaths.length === 0) {
    return {
      imagePaths: question.imagePaths ? [...question.imagePaths] : undefined,
      promptImages: question.promptImages
        ? question.promptImages.map((image) => ({ ...image }))
        : undefined,
    };
  }

  return {
    imagePaths: [],
    promptImages: [
      ...(question.promptImages ?? []).map((image) => ({ ...image })),
      ...legacyPaths.map((imageUrl, index) => ({
        id: `legacy-image-${index + 1}`,
        imageUrl,
      })),
    ],
  };
}

export function cloneEditableQuestion(question: EditableQuestion): EditableQuestion {
  return {
    ...question,
    detailedAnswerImages: question.detailedAnswerImages
      ? question.detailedAnswerImages.map((image) => ({ ...image }))
      : undefined,
    gradingRubric: cloneGradingRubric(question.gradingRubric),
    hintImages: question.hintImages
      ? question.hintImages.map((image) => ({ ...image }))
      : undefined,
    options: [...question.options],
    parts: question.parts?.map((part) => cloneQuestionPart(part)),
    tags: question.tags ? [...question.tags] : undefined,
    variables: question.variables.map((variable) => ({ ...variable })),
    ...foldLegacyImagePaths(question),
  };
}

export function createDraftQuestionPart(
  type: AddableQuestionPartType,
): EditableQuestionPart {
  const gradingRubric =
    type === 'AI_UPLOAD' ? createDraftGradingRubric() : undefined;

  return {
    contentHtml: '<p></p>',
    correctAnswerIndex: type === 'MULTIPLE_CHOICE' ? 0 : undefined,
    correctAnswerIndices: type === 'SELECT_ALL' ? [] : undefined,
    gradingRubric,
    id: createGeneratedId('question-part'),
    maxNumAttempts: type === 'AI_UPLOAD' ? 1 : undefined,
    options:
      type === 'MULTIPLE_CHOICE' || type === 'SELECT_ALL' ? ['', ''] : [],
    type,
    useCustomNumAttempts: type === 'AI_UPLOAD' ? true : undefined,
    value: type === 'AI_UPLOAD' ? getRubricTotalScore(gradingRubric) : 1,
    variableBasedAnswer: type === 'AI_UPLOAD' ? undefined : '',
  };
}

export function createDraftQuestion(
  type: AddableQuestionType = 'MULTIPLE_CHOICE',
): EditableQuestion {
  const gradingRubric =
    type === 'AI_UPLOAD' ? createDraftGradingRubric() : undefined;

  return {
    contentHtml: '<p></p>',
    correctAnswerIndex: type === 'MULTIPLE_CHOICE' ? 0 : undefined,
    correctAnswerIndices: type === 'SELECT_ALL' ? [] : undefined,
    creditForParticipation: false,
    id: createGeneratedId('question'),
    imagePaths: [],
    isNew: true,
    isPublic: true,
    gradingRubric,
    maxNumAttempts: type === 'AI_UPLOAD' ? 1 : undefined,
    options:
      type === 'MULTIPLE_CHOICE' || type === 'SELECT_ALL' ? ['', ''] : [],
    parts:
      type === 'MULTI_PART'
        ? [createDraftQuestionPart('EXACT_ANSWER')]
        : undefined,
    tags: [],
    type,
    useCustomNumAttempts: false,
    useRandomVariables: false,
    value: type === 'AI_UPLOAD' ? getRubricTotalScore(gradingRubric) : 1,
    variableBasedAnswer: '',
    variables: [],
  };
}

function cleanQuestionPartForType(
  part: EditableQuestionPart,
): EditableQuestionPart {
  const cleaned: EditableQuestionPart = cloneQuestionPart(part);

  // Both option-bearing types keep their options. Each clears the OTHER's
  // correct-answer field so a set of indices and a single index can never both
  // be live on one question.
  if (cleaned.type !== 'MULTIPLE_CHOICE' && cleaned.type !== 'SELECT_ALL') {
    cleaned.options = [];
  }

  if (cleaned.type !== 'MULTIPLE_CHOICE') {
    cleaned.correctAnswerIndex = undefined;
  }

  if (cleaned.type !== 'SELECT_ALL') {
    cleaned.correctAnswerIndices = undefined;
  }

  if (cleaned.type !== 'NUMERICAL_WITH_ERROR') {
    cleaned.numericalAnswer = undefined;
    cleaned.numericalError = undefined;
  }

  if (cleaned.type !== 'EXACT_ANSWER') {
    cleaned.exactAnswer = undefined;
  }

  if (cleaned.type === 'AI_UPLOAD') {
    cleaned.answerUnits = undefined;
    cleaned.gradingRubric = normalizeGradingRubric(cleaned.gradingRubric);
    cleaned.maxNumAttempts = cleaned.maxNumAttempts ?? 1;
    cleaned.useCustomNumAttempts = true;
    cleaned.useRandomVariables = false;
    cleaned.value = getRubricTotalScore(cleaned.gradingRubric);
    cleaned.variableBasedAnswer = undefined;
  } else {
    cleaned.gradingRubric = undefined;
    cleaned.maxNumAttempts = undefined;
    cleaned.useCustomNumAttempts = undefined;
  }

  return cleaned;
}

export function cleanQuestionForType(question: EditableQuestion): EditableQuestion {
  const cleaned = cloneEditableQuestion(question);

  // Both option-bearing types keep their options. Each clears the OTHER's
  // correct-answer field so a set of indices and a single index can never both
  // be live on one question.
  if (cleaned.type !== 'MULTIPLE_CHOICE' && cleaned.type !== 'SELECT_ALL') {
    cleaned.options = [];
  }

  if (cleaned.type !== 'MULTIPLE_CHOICE') {
    cleaned.correctAnswerIndex = undefined;
  }

  if (cleaned.type !== 'SELECT_ALL') {
    cleaned.correctAnswerIndices = undefined;
  }

  if (cleaned.type !== 'NUMERICAL_WITH_ERROR') {
    cleaned.numericalAnswer = undefined;
    cleaned.numericalError = undefined;
  }

  if (cleaned.type !== 'EXACT_ANSWER') {
    cleaned.exactAnswer = undefined;
  }

  if (cleaned.type === 'MULTI_PART') {
    cleaned.parts = cleaned.parts?.map((part) => cleanQuestionPartForType(part));
  } else {
    cleaned.parts = undefined;
  }

  if (cleaned.type === 'AI_UPLOAD') {
    cleaned.gradingRubric = normalizeGradingRubric(cleaned.gradingRubric);
    cleaned.value = getRubricTotalScore(cleaned.gradingRubric);
    cleaned.maxNumAttempts = cleaned.maxNumAttempts ?? 1;
    cleaned.useRandomVariables = false;
    cleaned.variables = [];
    cleaned.variableBasedAnswer = undefined;
  }

  return cleaned;
}

function getQuestionPartValidationError(part: EditableQuestionPart) {
  if (part.type === 'SELECT_ALL') {
    if (part.options.length < 2) {
      return 'Select-all parts need at least two options.';
    }

    if (part.options.some((option) => option.trim().length === 0)) {
      return 'Each option needs text before the part can be saved.';
    }

    const markedIndices = part.correctAnswerIndices ?? [];

    if (markedIndices.length === 0) {
      return 'Mark at least one correct option for this part.';
    }

    if (
      markedIndices.some((index) => index < 0 || index >= part.options.length)
    ) {
      return 'A marked correct option no longer exists. Re-mark the correct options.';
    }
  }

  if (part.type === 'MULTIPLE_CHOICE') {
    if (part.options.length < 2) {
      return 'Multiple-choice parts need at least two options.';
    }

    if (part.options.some((option) => option.trim().length === 0)) {
      return 'Each option needs text before the part can be saved.';
    }

    if (
      part.correctAnswerIndex === undefined ||
      part.correctAnswerIndex < 0 ||
      part.correctAnswerIndex >= part.options.length
    ) {
      return 'Choose the correct answer for this part.';
    }
  }

  if (part.type === 'NUMERICAL_WITH_ERROR') {
    if (
      !hasVariableBasedAnswer(part.variableBasedAnswer) &&
      (part.numericalAnswer === undefined || Number.isNaN(part.numericalAnswer))
    ) {
      return 'A numerical answer is required for this part.';
    }

    if (part.numericalError === undefined || Number.isNaN(part.numericalError)) {
      return 'An error tolerance is required for this part.';
    }
  }


  if (
    part.type === 'EXACT_ANSWER' &&
    !normalizeString(part.exactAnswer) &&
    !hasVariableBasedAnswer(part.variableBasedAnswer)
  ) {
    return 'An exact answer is required for this part.';
  }

  if (part.type === 'AI_UPLOAD') {
    const rubric = normalizeGradingRubric(part.gradingRubric);

    if (rubric.rubricUnits.length === 0) {
      return 'Add at least one rubric unit before saving this AI Upload part.';
    }

    if (rubric.rubricUnits.some((unit) => unit.name.trim().length === 0)) {
      return 'Each rubric unit needs a name before the part can be saved.';
    }

    if (
      rubric.rubricUnits.some(
        (unit) => !Number.isFinite(unit.score) || unit.score <= 0,
      )
    ) {
      return 'Each rubric unit needs a positive point value.';
    }
  }

  return undefined;
}

/**
 * How to write a randomized question's answer formula. "##v## * ##t##" could
 * not be computed, and nothing said why: the marks are how the question's text
 * shows a variable, and the formula names it.
 */
export const FORMULA_NAMES_HINT =
  "Write the formula with the variables' names, as v * t: the ##v## marks are only for the question's text.";

/** Why a formula could not be computed, with the hint when it holds ## marks. */
export function describeFormulaError(formula: string, error: string) {
  return formula.includes('##') ? `${error} ${FORMULA_NAMES_HINT}` : error;
}

/**
 * Confirms a randomized question's answer formula actually evaluates.
 *
 * Without this a formula that is mathematically right but syntactically wrong —
 * "arccos(Fx/F)*(180/pi())", where mathjs spells the function acos and treats pi
 * as a constant — saves happily, renders a perfectly normal question, computes
 * no answer, and grades every student wrong. Nothing anywhere said so, because
 * the resolver retries and discards its errors.
 *
 * Checked against the same representative values the preview card shows, so the
 * message names numbers the instructor can see on screen.
 */
function getAnswerFormulaError(
  question: EditableQuestion,
  formula: string | undefined,
  subject: 'question' | 'part',
): string | undefined {
  if (!question.useRandomVariables || !hasVariableBasedAnswer(formula)) {
    return undefined;
  }

  const values = resolveRepresentativeVariableValues(
    {
      content: question.contentHtml,
      seed_for_random_variables: question.seedForRandomVariables,
      variable_based_answer: formula,
      // The editor only authors NUMERICAL variables; a random-set question's
      // paired content is preserved but not editable here.
      variables: question.variables.map((variable) => ({
        max_value: variable.maxValue,
        min_value: variable.minValue,
        name: variable.name,
        type: variable.type,
      })),
    } as unknown as RawQuestion,
    question.type === 'MULTI_PART',
  );

  // A random-set question picks paired content rather than evaluating a
  // formula, so there is nothing to check.
  if (values.length === 0) {
    return undefined;
  }

  const { error } = evaluateVariableBasedAnswer(formula, values);

  return error && formula
    ? `The answer formula for this ${subject} could not be evaluated: ${describeFormulaError(formula, error)}`
    : undefined;
}

function getQuestionValidationError(question: EditableQuestion) {
  if (question.type === 'UNSUPPORTED') {
    return undefined;
  }

  if (question.type === 'SELECT_ALL') {
    if (question.options.length < 2) {
      return 'Select-all questions need at least two options.';
    }

    if (question.options.some((option) => option.trim().length === 0)) {
      return 'Each option needs text before the question can be saved.';
    }

    const markedIndices = question.correctAnswerIndices ?? [];

    if (markedIndices.length === 0) {
      return 'Mark at least one correct option for this question.';
    }

    if (
      markedIndices.some(
        (index) => index < 0 || index >= question.options.length,
      )
    ) {
      return 'A marked correct option no longer exists. Re-mark the correct options.';
    }
  }

  if (question.type === 'MULTIPLE_CHOICE') {
    if (question.options.length < 2) {
      return 'Multiple-choice questions need at least two options.';
    }

    if (question.options.some((option) => option.trim().length === 0)) {
      return 'Each option needs text before the question can be saved.';
    }

    if (
      question.correctAnswerIndex === undefined ||
      question.correctAnswerIndex < 0 ||
      question.correctAnswerIndex >= question.options.length
    ) {
      return 'Choose the correct answer for this question.';
    }
  }

  if (question.type === 'NUMERICAL_WITH_ERROR') {
    if (
      !hasVariableBasedAnswer(question.variableBasedAnswer) &&
      (question.numericalAnswer === undefined || Number.isNaN(question.numericalAnswer))
    ) {
      return 'A numerical answer is required for this question.';
    }

    if (question.numericalError === undefined || Number.isNaN(question.numericalError)) {
      return 'An error tolerance is required for this question.';
    }

    const formulaError = getAnswerFormulaError(
      question,
      question.variableBasedAnswer,
      'question',
    );

    if (formulaError) {
      return formulaError;
    }
  }

  if (
    question.type === 'EXACT_ANSWER' &&
    !normalizeString(question.exactAnswer) &&
    !hasVariableBasedAnswer(question.variableBasedAnswer)
  ) {
    return 'An exact answer is required for this question.';
  }

  if (question.type === 'MULTI_PART' && (!question.parts || question.parts.length === 0)) {
    return 'Add at least one part before saving this multi-part question.';
  }

  if (question.type === 'AI_UPLOAD') {
    const rubric = normalizeGradingRubric(question.gradingRubric);

    if (rubric.rubricUnits.length === 0) {
      return 'Add at least one rubric unit before saving this AI Upload question.';
    }

    if (rubric.rubricUnits.some((unit) => unit.name.trim().length === 0)) {
      return 'Each rubric unit needs a name before the question can be saved.';
    }

    if (
      rubric.rubricUnits.some(
        (unit) => !Number.isFinite(unit.score) || unit.score <= 0,
      )
    ) {
      return 'Each rubric unit needs a positive point value.';
    }
  }

  return undefined;
}

export function validateQuestionDraft(question: EditableQuestion): EditableQuestion {
  const validationError = getQuestionValidationError(question);
  const parts = question.parts?.map((part) => ({
    ...part,
    validationError: getQuestionPartValidationError(part),
  }));

  return {
    ...question,
    parts,
    validationError:
      validationError ??
      (parts?.some((part) => part.validationError)
        ? 'Fix the invalid part fields before saving.'
        : undefined),
  };
}

export function getQuestionTypeLabel(type: EditableQuestion['type']) {
  if (type === 'MULTIPLE_CHOICE') {
    return 'Multiple Choice';
  }

  if (type === 'SELECT_ALL') {
    return 'Select All That Apply';
  }

  if (type === 'NUMERICAL_WITH_ERROR') {
    return 'Numerical';
  }

  if (type === 'EXACT_ANSWER') {
    return 'Exact Answer';
  }

  if (type === 'MULTI_PART') {
    return 'Multi-Part';
  }

  if (type === 'AI_UPLOAD') {
    return 'AI Upload';
  }

  return 'Unsupported';
}

export function isEditableQuestionType(
  value: EditableQuestion['type'],
): value is SupportedQuestionType {
  return value !== 'UNSUPPORTED';
}
